import React, { useState, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';
import { Data, Layout, Config } from 'plotly.js';

const SankeyDiagram: React.FC = () => {
  const [preparationLevel, setPreparationLevel] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  
  // Calculate success/failure rates based on preparation level
  // At 0% prep: 26% success, 74% failure
  // At 100% prep: 78% success, 22% failure
  const successRate = 26 + (preparationLevel / 100) * 52; // Linear interpolation from 26% to 78%
  const failureRate = 100 - successRate;
  
  // Calculate intermediate flows based on preparation level
  const earlyAdopterRate = 20 + (preparationLevel / 100) * 30; // 20% to 50%
  const resistanceRate = 80 - (preparationLevel / 100) * 30; // 80% to 50%
  
  // From early adopters: higher success rate
  const earlyAdopterSuccess = 60 + (preparationLevel / 100) * 35; // 60% to 95%
  const earlyAdopterFailure = 100 - earlyAdopterSuccess;
  
  // From resistance: lower success rate
  const resistanceSuccess = 10 + (preparationLevel / 100) * 50; // 10% to 60%
  const resistanceFailure = 100 - resistanceSuccess;
  
  // Calculate actual flows
  const earlyAdopterToSuccess = (earlyAdopterRate * earlyAdopterSuccess) / 100;
  const earlyAdopterToFailure = (earlyAdopterRate * earlyAdopterFailure) / 100;
  const resistanceToSuccess = (resistanceRate * resistanceSuccess) / 100;
  const resistanceToFailure = (resistanceRate * resistanceFailure) / 100;
  
  const data: Data[] = [{
    type: "sankey",
    orientation: "h",
    node: {
      pad: 15,
      thickness: 30,
      line: {
        color: "black",
        width: 0.5
      },
      label: [
        "All Organizations<br>Attempting AI", 
        "Early Adopters<br>& Champions", 
        "Resistance &<br>Skepticism",
        "AI Success<br>🚀", 
        "AI Failure<br>⚠️"
      ],
      color: [
        "#6b7280", // Gray for starting
        "#10b981", // Green for early adopters
        "#f59e0b", // Amber for resistance
        "#059669", // Dark green for success
        "#dc2626"  // Red for failure
      ],
      x: [0.01, 0.4, 0.4, 0.99, 0.99],
      y: [0.5, 0.3, 0.7, 0.2, 0.8]
    },
    link: {
      source: [0, 0, 1, 1, 2, 2], // From nodes
      target: [1, 2, 3, 4, 3, 4], // To nodes
      value: [
        earlyAdopterRate,
        resistanceRate,
        earlyAdopterToSuccess,
        earlyAdopterToFailure,
        resistanceToSuccess,
        resistanceToFailure
      ],
      color: [
        "rgba(16, 185, 129, 0.4)", // To early adopters
        "rgba(245, 158, 11, 0.4)", // To resistance
        "rgba(5, 150, 105, 0.4)",  // Early adopters to success
        "rgba(220, 38, 38, 0.4)",  // Early adopters to failure
        "rgba(5, 150, 105, 0.4)",  // Resistance to success
        "rgba(220, 38, 38, 0.4)"   // Resistance to failure
      ]
    }
  }];
  
  const layout: Partial<Layout> = {
    title: {
      text: "AI Transformation Success Rates",
      font: {
        size: 24,
        color: '#1f2937'
      }
    },
    font: {
      size: 12,
      color: '#4b5563'
    },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    margin: {
      l: 20,
      r: 20,
      t: 80,
      b: 20
    },
    annotations: [
      {
        x: 0.01,
        y: 1.1,
        text: `<b>100%</b><br>of organizations`,
        showarrow: false,
        font: { size: 14 }
      },
      {
        x: 0.99,
        y: 1.1,
        text: `<b>${successRate.toFixed(0)}%</b> Success<br><b>${failureRate.toFixed(0)}%</b> Failure`,
        showarrow: false,
        font: { 
          size: 16,
          color: preparationLevel > 50 ? '#059669' : '#dc2626'
        }
      }
    ]
  };
  
  const config: Partial<Config> = {
    responsive: true,
    displayModeBar: false
  };
  
  // Preparation strategies that unlock at different levels
  const strategies = [
    { level: 0, text: "❌ No change management plan", color: "text-red-600", isNegative: true },
    { level: 20, text: "✅ Team I AM Assessment completed - change readiness understood", color: "text-green-600" },
    { level: 35, text: "✅ Champions identified and activated", color: "text-green-600" },
    { level: 50, text: "✅ Communications tailored to change profiles", color: "text-green-600" },
    { level: 65, text: "✅ Leadership training with I AM insights", color: "text-green-600" },
    { level: 80, text: "✅ Continuous support & feedback loops", color: "text-green-600" },
    { level: 100, text: "🌟 Full human-centered transformation achieved", color: "text-green-700" }
  ];
  
  const activeStrategies = preparationLevel === 0 
    ? strategies.filter(s => s.level === 0)  // Only show "No plan" at 0%
    : strategies.filter(s => s.level <= preparationLevel && s.level > 0); // Show unlocked positive strategies
  
  // Auto-animate the slider when component comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated && !isUserInteracting) {
            setHasAnimated(true);
            
            // Start animation after a short delay
            setTimeout(() => {
              if (!isUserInteracting) {
                let currentLevel = 0;
                const animationInterval = setInterval(() => {
                  if (currentLevel >= 75 || isUserInteracting) {
                    clearInterval(animationInterval);
                    
                    // Pause at 75% to show good results
                    setTimeout(() => {
                      if (!isUserInteracting) {
                        // Animate back to 0
                        const returnInterval = setInterval(() => {
                          if (currentLevel <= 0 || isUserInteracting) {
                            clearInterval(returnInterval);
                          } else {
                            currentLevel -= 3;
                            if (!isUserInteracting) {
                              setPreparationLevel(Math.max(0, currentLevel));
                            }
                          }
                        }, 30);
                      }
                    }, 2000); // Pause for 2 seconds at 75%
                  } else {
                    currentLevel += 2;
                    if (!isUserInteracting) {
                      setPreparationLevel(currentLevel);
                    }
                  }
                }, 40); // Smooth animation
              }
            }, 1000); // Wait 1 second before starting
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, [hasAnimated, isUserInteracting]);
  
  return (
    <div className="w-full" ref={componentRef}>
      <Plot
        data={data}
        layout={layout}
        config={config}
        className="w-full"
        style={{ height: '500px' }}
      />
      
      <div className="mt-8 bg-gray-50 p-6 rounded-lg">
        <div className="mb-6">
          <label className="block text-lg font-bold text-gray-900 mb-3">
            Change Preparation Level: {preparationLevel}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={preparationLevel}
            onChange={(e) => {
              setIsUserInteracting(true);
              setPreparationLevel(parseInt(e.target.value));
            }}
            onMouseDown={() => setIsUserInteracting(true)}
            onTouchStart={() => setIsUserInteracting(true)}
            className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${preparationLevel}%, #e5e7eb ${preparationLevel}%, #e5e7eb 100%)`
            }}
          />
          <div className="flex justify-between text-sm text-gray-600 mt-2">
            <span>Traditional Approach</span>
            <span>Human-Centered Approach</span>
          </div>
          {!hasAnimated && !isUserInteracting && (
            <div className="text-center mt-2 text-sm text-teal-600 animate-pulse">
              Watch how preparation level impacts success rates →
            </div>
          )}
          {hasAnimated && !isUserInteracting && preparationLevel === 0 && (
            <div className="text-center mt-2 text-sm text-gray-500">
              Try moving the slider to see the impact of preparation
            </div>
          )}
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold text-gray-900 mb-3">Active Strategies:</h3>
            <ul className="space-y-2">
              {strategies.map((strategy, i) => {
                const isActive = preparationLevel === 0 
                  ? strategy.level === 0 
                  : strategy.level <= preparationLevel && strategy.level > 0;
                const shouldShow = preparationLevel === 0 
                  ? strategy.level === 0 
                  : strategy.level > 0;
                
                if (!shouldShow) return null;
                
                return (
                  <li key={i} className={`text-sm flex items-start transition-all duration-300 ${isActive ? 'opacity-100 transform translate-x-0' : 'opacity-30 transform -translate-x-2'}`}>
                    <span className={`mr-2 ${isActive ? strategy.color : 'text-gray-400'}`}>
                      {strategy.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="font-bold text-gray-900 mb-3">Key Insights:</h3>
            <div className="space-y-3">
              <div className="text-sm">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-600">Early Adopters</span>
                  <span className="font-semibold">{earlyAdopterRate.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${earlyAdopterRate}%` }}
                  />
                </div>
              </div>
              
              <div className="text-sm">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-gray-600">Overall Success Rate</span>
                  <span className={`font-bold text-lg ${successRate > 50 ? 'text-green-600' : 'text-red-600'}`}>
                    {successRate.toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-500 ${successRate > 50 ? 'bg-green-600' : 'bg-red-600'}`}
                    style={{ width: `${successRate}%` }}
                  />
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-200">
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex items-center">
                    <span className="mr-2">📊</span>
                    <span>Increase of {((successRate - 26) / 26 * 100).toFixed(0)}% from baseline</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">💡</span>
                    <span>{preparationLevel < 50 ? 'Most resistance, low adoption' : 'Champions driving change'}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">⏱️</span>
                    <span>ROI timeline: {preparationLevel < 50 ? '18-24 months' : '6-12 months'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {preparationLevel < 25 && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                ⚠️ Critical: Without preparation, failure is almost certain
              </div>
            )}
            {preparationLevel >= 25 && preparationLevel < 50 && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                ⚡ Some progress, but still high risk of failure
              </div>
            )}
            {preparationLevel >= 50 && preparationLevel < 75 && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                📈 Tipping point reached - momentum building
              </div>
            )}
            {preparationLevel >= 75 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                🎯 With human-centered preparation, success becomes the norm
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SankeyDiagram;